<template>
    <GLText
        ref="refText"
        :text="textWrap"
        :layer="LAYER_SCENE"
        :options="textOptions"
        @sync="onTextSync"
        name="TextWrap"
        :material="material"
    ></GLText>
</template>

<script setup>
    import { computed, onMounted, reactive, ref, watch } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { usePane, useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import GLText from '~/components/gl/GLText.vue';
    import { setupTextWrapMaterial } from '~/components/gl/materials/TextWrapMaterial';
    import { LAYER_SCENE } from '~/core/constants';
    import { formatCategoryText } from '~/utils/text-utils';

    const props = defineProps({
        debug: { default: false },
        text: { default: 'Chase' },
        renderOrder: { default: 1 },
        scale: { default: 0.7 },
        options: { default: () => ({}) },
    });

    const refText = ref(null);
    const visible = ref(false);

    const MAX_TEXT_LENGTH = 24;

    const textWrap = computed(() => {
        const text = props.text.trim();
        if (!text) return '';

        const base = `${text} `;
        const totalRepeat = Math.max(1, Math.ceil(MAX_TEXT_LENGTH / base.length));

        return base.repeat(totalRepeat);
    });

    const { object } = useThreeObject(null, {
        name: 'TextWrap',
        props: { v: visible, s: props.scale },
    });

    const fontSize = 1;
    const progress = ref(0);
    const uRadius = ref(1);

    const textOptions = reactive({
        fontSize,
        anchorX: 'center',
        anchorY: 'middle',
        color: 'white',
        ...props.options,
    });

    onMounted(() => {
        const { mesh } = refText.value;

        mesh.renderOrder = props.renderOrder;
    });

    const material = {
        fn: setupTextWrapMaterial,
        uniforms: {
            uRadius: { value: uRadius.value },
        },
    };

    const onTextSync = () => {
        // Setup material after text is synced and Troika has initialized geometry
        // const { mesh } = refText.value;
        // const uniforms = { uRadius: { value: uRadius.value } };
        // setupTextWrapMaterial(mesh.material, uniforms);

        updateLetters();
    };

    const updateLetters = () => {
        refText.value.updateLetters();
    };

    let tl;
    const show = ({ delay = 0, timeScale = 1, ambientDist = 0.02 } = {}) => {
        tl?.kill();

        const tweens = {
            inPr: 0,
            ambientPr: 0,
            outPr: 0,
        };

        tl = new gsap.timeline({
            delay,
            onStart: () => (visible.value = true),
            onComplete: () => (visible.value = false),
            onUpdate: () => {
                // updateLetters();

                progress.value =
                    lerp(tweens.inPr, 0.0, 0.5) +
                    lerp(tweens.outPr, 0.0, 0.5) +
                    lerp(tweens.ambientPr, -ambientDist, ambientDist);
            },
        });

        const inDur = 0.7;
        const outDur = 0.4;
        const outDelay = 0.8;
        // const outDelay = inDur;
        const inEase = 'power2.out';
        const outEase = 'power2.in';
        const ambientDur = outDelay + outDur;

        tl.addLabel('start', 0);
        tl.addLabel('end', outDelay);

        // const { letters } = refText.value;

        tl.fromTo(tweens, { inPr: 0 }, { inPr: 1, duration: inDur, ease: inEase }, 'start');
        tl.fromTo(
            tweens,
            { ambientPr: 0 },
            { ambientPr: 1, duration: ambientDur, ease: 'none' },
            'start'
        );
        tl.fromTo(tweens, { outPr: 0 }, { outPr: 1, duration: outDur, ease: outEase }, `end`);

        tl.timeScale(timeScale);

        return tl;
    };

    watch(progress, (v) => {
        const { mesh } = refText.value;
        if (mesh.material.uniforms.uProgress) mesh.material.uProgress = v;
    });

    watch(uRadius, (v) => {
        const { mesh } = refText.value;
        if (mesh.material.uniforms.uRadius) mesh.material.uRadius = v;
    });

    defineExpose({ show, object, text: refText, updateLetters });

    usePane(
        [
            { name: 'progress', value: progress, options: { min: 0, max: 1, step: 0.001 } },
            { name: 'uRadius', value: uRadius, options: { min: 0, max: 1, step: 0.001 } },
        ],
        {
            title: 'TextWrap',
        }
    );
</script>
